import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, MapPin, ArrowRight, DollarSign } from "lucide-react";
import { useI18n } from "../contexts/LanguageContext";
import api from "../lib/api";
import { Input } from "../components/ui/input";
import useSeo from "../hooks/useSeo";

const TYPES = ["Full-time", "Part-time", "Seasonal", "Contract"];

export default function Jobs() {
  const { t, lang } = useI18n();
  const [jobs, setJobs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [type, setType] = useState("");

  useSeo({
    title: lang === "es" ? "Empleos en NJ - Logística, Manufactura y más" : "Jobs in NJ - Logistics, Manufacturing & more",
    description: lang === "es"
      ? "Encuentra tu próximo empleo en New Jersey. Cientos de oportunidades en logística, manufactura, administración y transporte."
      : "Find your next job in New Jersey. Hundreds of openings in logistics, manufacturing, administration and transportation.",
    path: "/jobs",
  });

  useEffect(() => { api.get("/jobs/categories").then((r) => setCategories(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    const params = {};
    if (search) params.search = search;
    if (category) params.category = category;
    if (type) params.type = type;
    api.get("/jobs", { params }).then((r) => setJobs(r.data)).catch(() => {});
  }, [search, category, type]);

  const fmt = useMemo(() => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }), []);

  return (
    <div data-testid="page-jobs">
      {/* HERO */}
      <section className="bg-navy text-white">
        <div className="container-pad mx-auto max-w-[1400px] py-20 md:py-24">
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight" data-testid="jobs-title">{t.jobs.heroTitle}</h1>
          <p className="mt-3 text-white/70 text-lg max-w-2xl">{t.jobs.heroSubtitle}</p>
          <div className="mt-8 bg-white rounded-2xl p-3 flex items-center gap-2 max-w-2xl shadow-2xl">
            <Search className="text-slate-400 ml-2" size={20} />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t.jobs.searchPlaceholder}
              className="border-0 bg-transparent focus-visible:ring-0 text-navy placeholder:text-slate-400 text-base"
              data-testid="jobs-search-input"
            />
          </div>
        </div>
      </section>

      {/* FILTERS */}
      <section className="container-pad mx-auto max-w-[1400px] py-8">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs tracking-widest uppercase text-slate-500 mr-2">{t.common.category}:</span>
          <button onClick={() => setCategory("")} className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${category === "" ? "bg-navy text-white" : "bg-soft text-slate-600 hover:bg-slate-200"}`} data-testid="filter-cat-all">{t.jobs.allCategories}</button>
          {categories.map((c) => (
            <button key={c} onClick={() => setCategory(c === category ? "" : c)} className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${category === c ? "bg-navy text-white" : "bg-soft text-slate-600 hover:bg-slate-200"}`} data-testid={`filter-cat-${c.toLowerCase().replace(/\s+/g, "-")}`}>{c}</button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2 mt-3">
          <span className="text-xs tracking-widest uppercase text-slate-500 mr-2">{t.common.type}:</span>
          <button onClick={() => setType("")} className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${type === "" ? "bg-navy text-white" : "bg-soft text-slate-600 hover:bg-slate-200"}`} data-testid="filter-type-all">{t.jobs.allTypes}</button>
          {TYPES.map((tp) => (
            <button key={tp} onClick={() => setType(tp === type ? "" : tp)} className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${type === tp ? "bg-navy text-white" : "bg-soft text-slate-600 hover:bg-slate-200"}`} data-testid={`filter-type-${tp.toLowerCase()}`}>{tp}</button>
          ))}
        </div>
      </section>

      {/* JOBS GRID */}
      <section className="container-pad mx-auto max-w-[1400px] pb-20">
        {jobs.length === 0 ? (
          <div className="py-20 text-center text-slate-500" data-testid="jobs-empty">{t.common.noResults}</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {jobs.map((j) => (
              <Link key={j.job_id} to={`/job/${j.job_id}`} className="group bg-white border border-slate-100 rounded-[24px] p-7 hover:shadow-xl hover:-translate-y-1 transition-all" data-testid={`job-card-${j.job_id}`}>
                <div className="flex items-center justify-between text-xs">
                  <span className="px-3 py-1 rounded-full bg-soft text-slate-600 font-semibold">{j.category}</span>
                  <span className="px-3 py-1 rounded-full bg-brandred/10 text-brandred font-semibold">{j.type}</span>
                </div>
                <h3 className="mt-4 text-xl font-bold text-navy group-hover:text-brandred transition-colors">{j.title[lang] || j.title.en}</h3>
                <div className="mt-1 text-sm text-slate-600">{j.company}</div>
                <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-slate-600">
                  <span className="inline-flex items-center gap-1.5"><MapPin size={14} /> {j.location}</span>
                  <span className="inline-flex items-center gap-1.5"><DollarSign size={14} /> {fmt.format(j.salary_min)} – {fmt.format(j.salary_max)}</span>
                </div>
                <div className="mt-5 flex items-center justify-between">
                  <span className="text-sm font-semibold text-navy">{t.jobs.details}</span>
                  <span className="w-9 h-9 rounded-full bg-soft group-hover:bg-navy group-hover:text-white inline-flex items-center justify-center transition-colors"><ArrowRight size={16} /></span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
